// public/v3/src/lib/billing-generate.js — single-patient AI billing-note
// generation + persistence, ported verbatim from v2's generateBillingNote +
// saveBillingNote.
//
// Sources (v2 line numbers in public/ccm-compliance.js):
//   - generateBillingNote          → 6648-6710
//   - saveBillingNote              → 6713-6735
//   - buildBillingNoteAIPrompt     → 6407-6409
//   - assembleCombinedBillingNote  → 6525-6633
//   - getMonthlyActivitySummary    → 6289-6314
//   - getCcmBillingCodes           → 6317-6339
//   - bnBuildPromptVars            → 13125-13156
//   - bnBuildSectionInstructions   → 13094-13121
//   - bnGetTpl                     → 13070-13073
//   - getBillingNoteCarePlan       → 5724-5732
//   - ccmChronicConditionCount     → 31-37
//   - _resolveStaffCredential      → 10586-10592
//   - _formatStaffWithCredential   → 10595-10600
//   - conditionsToString           → 25-28
//   - CCM_TIERS                    → 6258-6277
//   - BILLING_TIME_TARGETS         → 7830-7849
//   - DEFAULT_RATES                → 7784-7827
//   - ADDON_CONFIG                 → 1830-1839
//   - BN_COMBINED_KEY              → 6643
//   - formatCpMonth                → 5762-5766
//   - STAFF_CREDENTIALS            → 10562
//
// v2 → v3 adaptations (allowed-change list only):
//   - getPatient(id) → patient passed in by caller
//   - DOM reads (bn-patient, bn-month, btn, time-override, preview) → omitted
//     (caller drives UX state)
//   - _bnHistory, window._bnGeneratedNotes globals → state passed through the
//     port and returned to caller
//   - _fetchWithTimeout / dbPost → ccmApi wrappers
//   - practiceSettings global → loaded from ccmApi.getSettings().practiceSettings
//   - staffList global → loaded from ccmApi.listStaff()
//   - _cpHistory (for getBillingNoteCarePlan) → loaded via ccmApi.listCarePlans

import { ccmApi, api } from './api';
import { invalidateDraftCache } from './draft';
import { BN_DEFAULT, BHI_BN_DEFAULT } from './v2-prompts';
import {
  BHI_BN_KEY,
  bhiGatesForCode,
  assembleBhiBillingNote,
  buildBhiAddonAttestationLines,
  entryProgramFamily,
  _latestAssessmentForMonth,
} from './bhi-billing';
import { isPainEnrolledPatient, painGatesForMonth, resolvePainCodes, buildPainAttestationLines } from './pain-billing';
import { isPinEnrolledPatient, pinGatesForMonth, resolvePinCodes, buildPinAttestationLines } from './pin-billing';
import { dedupeMedicationsByDrug, medicationsArrayToPromptString } from './medication-dedupe';
import { promptSafeLine } from './prompt-text';
import { safeAudit } from './consent-audit';
import { effectiveCcmCodeForMonth } from './ccm-tier-bump';
import { PCM_BASE_CODES, pcmPrincipalCondition } from './pcm-principal';
import { buildUserMessages } from './ai-messages.cjs';
import { buildMaterializedNote } from './batch-materialize.cjs';
import { carePlanOnFileForMonth } from './batch-careplan-keys.cjs';

// ── Verbatim v2 constants ────────────────────────────────────────────────────

// v2 source: STAFF_CREDENTIALS (ccm-compliance.js:10562)
const STAFF_CREDENTIALS = ['none','MA','CMA','RMA','CCMA','LPN','RN','NP','PA','MD'];

// v2 source: ADDON_CONFIG (ccm-compliance.js:1830-1839)
const ADDON_CONFIG = [
  { base: '99490', addonCode: '99439', max: 2 },
  { base: '99491', addonCode: '99437', max: 2 },
  { base: '99487', addonCode: '99489', max: Infinity }, // CMS publishes no monthly unit cap for 99489
  { base: '99492', addonCode: '99494', max: 4 },
  { base: '99493', addonCode: '99494', max: 4 },
  { base: 'G3002', addonCode: 'G3003', max: 4 },
  { base: '99424', addonCode: '99425', max: 2 },
  { base: '99426', addonCode: '99427', max: 2 },
  { base: 'G0556', addonCode: 'G0570', max: 1 },
  { base: 'G0557', addonCode: 'G0570', max: 1 },
  { base: 'G0558', addonCode: 'G0570', max: 1 }
];

// v2 source: DEFAULT_RATES (ccm-compliance.js:7784-7827)
const DEFAULT_RATES = {
  '99490':{desc:'CCM Staff, 1st 20 min/month',rate:76.02,addOn:false,program:'CCM'},
  '99439':{desc:'CCM Staff, each addl 20 min',rate:58.16,addOn:true,program:'CCM'},
  '99491':{desc:'CCM Physician, 1st 30 min/month',rate:101.78,addOn:false,program:'CCM'},
  '99437':{desc:'CCM Physician, each addl 30 min',rate:72.45,addOn:true,program:'CCM'},
  '99487':{desc:'Complex CCM, 1st 60 min/month',rate:166.91,addOn:false,program:'CCM'},
  '99489':{desc:'Complex CCM, each addl 30 min',rate:90.32,addOn:true,program:'CCM'},
  'G0556':{desc:'APCM Level 1 (0-1 chronic conditions)',rate:18.86,addOn:false,program:'APCM'},
  'G0557':{desc:'APCM Level 2 (2+ chronic conditions)',rate:61.84,addOn:false,program:'APCM'},
  'G0558':{desc:'APCM Level 3 (QMB-eligible, 2+ chronic conditions)',rate:135.10,addOn:false,program:'APCM'},
  'G0570':{desc:'APCM-paired BHI Care Mgmt, per calendar month',rate:65.71,addOn:true,program:'APCM'},
  '99484':{desc:'BHI Care Mgmt, per calendar month',rate:65.76,addOn:false,program:'Psych'},
  '99492':{desc:'CoCM Initial month',rate:185.90,addOn:false,program:'Psych'},
  '99493':{desc:'CoCM Subsequent month',rate:166.92,addOn:false,program:'Psych'},
  '99494':{desc:'CoCM add-on per month',rate:70.99,addOn:true,program:'Psych'},
  'G2214':{desc:'Integrated Psych Care, 1st 30 min',rate:70.40,addOn:false,program:'Psych'},
  'G0568':{desc:'Integrated Psych Care Mgmt — Initial',rate:187.71,addOn:false,program:'Psych'},
  'G0569':{desc:'Integrated Psych Care Mgmt — Subs.',rate:168.34,addOn:false,program:'Psych'},
  'G3002':{desc:'Chronic Pain Mgmt, 30 min/month',rate:99.08,addOn:false,program:'Pain'},
  'G3003':{desc:'Chronic Pain Mgmt, add-on 15 min',rate:36.76,addOn:true,program:'Pain'},
  '99424':{desc:'PCM Physician, 1st 30 min/month',rate:100.45,addOn:false,program:'PCM'},
  '99425':{desc:'PCM Physician, each addl 30 min',rate:70.46,addOn:true,program:'PCM'},
  '99426':{desc:'PCM Staff, 1st 30 min/month',rate:78.01,addOn:false,program:'PCM'},
  '99427':{desc:'PCM Staff, each addl 30 min',rate:62.49,addOn:true,program:'PCM'},
  '99453':{desc:'RPM Setup & Patient Education',rate:26.26,addOn:false,program:'RPM'},
  '99454':{desc:'RPM Device Supply, 16-30 days',rate:62.18,addOn:false,program:'RPM'},
  '99457':{desc:'RPM Tx Mgmt, 1st 20 min/month',rate:59.90,addOn:false,program:'RPM'},
  '98975':{desc:'RTM Setup & Patient Education',rate:26.26,addOn:false,program:'RPM'},
  '98976':{desc:'RTM Device Supply (respiratory)',rate:62.18,addOn:false,program:'RPM'},
  '98977':{desc:'RTM Device Supply (musculoskeletal)',rate:61.38,addOn:false,program:'RPM'},
  '98980':{desc:'RTM Tx Mgmt, 1st 20 min/month',rate:62.42,addOn:false,program:'RPM'},
  '99091':{desc:'Physiologic Data Collection/Interp.',rate:62.89,addOn:false,program:'RPM'},
  'G0023':{desc:'Principal Illness Nav, 60 min/month',rate:101.04,addOn:false,program:'Nav'},
  'G0024':{desc:'Principal Illness Nav, addl 30 min',rate:62.93,addOn:true,program:'Nav'},
  'G0140':{desc:'Navigation Peer Support, 60 min/month',rate:103.42,addOn:false,program:'Nav'},
  'G0146':{desc:'Navigation Peer Support, addl 30 min',rate:61.51,addOn:true,program:'Nav'},
};

// v2 source: BILLING_TIME_TARGETS (ccm-compliance.js:7830-7849)
const BILLING_TIME_TARGETS = {
  '99490':20, '99491':30, '99487':60,
  '99439':20, '99437':30, '99489':30,
  'G0556':0, 'G0557':0, 'G0558':0, 'G0570':0,
  '99484':20, '99492':70, '99493':60,
  'G2214':30, 'G0568':70, 'G0569':60,
  '99494':30,
  'G3002':30, 'G3003':15,
  '99424':30, '99426':30, '99425':30, '99427':30,
  '99457':20, '98980':20,
  'G0023':60, 'G0140':60, 'G0024':30, 'G0146':30
};

// v2 source: CCM_TIERS (ccm-compliance.js:6258-6277)
const CCM_TIERS = {
  '99490': {
    name: 'Chronic Care Management (99490)',
    tier: 'Standard CCM',
    enrollment: 'Patient enrolled in Chronic Care Management (CCM) for ongoing management of multiple chronic conditions. Care management services are provided by clinical staff under the direction of the billing practitioner. Conditions are expected to last >12 months and place the patient at risk for acute exacerbation, hospitalization, and functional decline.',
    complianceExtra: []
  },
  '99491': {
    name: 'Chronic Care Management — Practitioner-Led (99491)',
    tier: 'Practitioner-Led CCM',
    enrollment: 'Patient enrolled in physician/qualified health professional-directed Chronic Care Management (CCM). At least 50% of CCM time this month was provided personally by the physician or other qualified health professional. Conditions are expected to last >12 months and require comprehensive care management.',
    complianceExtra: ['Physician/QHP personally provided at least 50% of CCM time and directed the care management activities.']
  },
  '99487': {
    name: 'Complex Chronic Care Management (99487)',
    tier: 'Complex CCM',
    enrollment: 'Patient enrolled in Complex Chronic Care Management requiring moderate-to-high complexity medical decision making for multiple chronic conditions. Patient\'s clinical complexity requires substantial coordination and monitoring beyond standard CCM. Conditions are expected to last >12 months and place the patient at significant risk for acute exacerbation, hospitalization, and functional decline.',
    complianceExtra: ['By signing this note, the billing practitioner attests that medical decision making for this patient was of moderate-to-high complexity due to the nature, number, and severity of conditions managed.']
  }
};

// APCM tiers — per NACHC March 2026 + CMS PFS 2026. Mirrors CCM_TIERS shape so
// assembleCombinedBillingNote can swap in APCM-flavored enrollment text and
// compliance attestation when an APCM fallback decision exists for the month.
const APCM_TIERS = {
  'G0556': {
    name: 'Advanced Primary Care Management — Level 1 (G0556)',
    tier: 'APCM Level 1',
    enrollment: 'Advanced Primary Care Management (APCM) Level 1 was selected for this billing period based on patient eligibility and the billing provider\'s care-management election. Level 1 (G0556) applies to patients with zero or one chronic condition.',
    complianceExtra: ['APCM billed under G0556 — Level 1 (0-1 chronic conditions).']
  },
  'G0557': {
    name: 'Advanced Primary Care Management — Level 2 (G0557)',
    tier: 'APCM Level 2',
    enrollment: 'Advanced Primary Care Management (APCM) Level 2 was selected for this billing period for longitudinal primary care management. Level 2 (G0557) applies to patients with two or more chronic conditions expected to last more than 12 months.',
    complianceExtra: ['APCM billed under G0557 — Level 2 (2+ chronic conditions).']
  },
  'G0558': {
    name: 'Advanced Primary Care Management — Level 3 (G0558)',
    tier: 'APCM Level 3',
    enrollment: 'Advanced Primary Care Management (APCM) Level 3 was selected for this billing period for longitudinal primary care management. Level 3 (G0558) applies to QMB-eligible patients with two or more chronic conditions; Medicare cost-sharing does not apply.',
    complianceExtra: ['APCM billed under G0558 — Level 3 (QMB-eligible, 2+ chronic conditions).', 'Patient is QMB-eligible; no Medicare cost-sharing applies.']
  }
};

// PCM tiers — Principal Care Management (CY2026 PFS). One complex chronic
// condition expected to last at least 3 months, disease-specific care plan,
// 30-minute monthly threshold. Same-practitioner PCM and CCM are mutually
// exclusive per patient-month (structurally enforced: one base program), and
// PCM is bundled into APCM — PCM enrollees never get APCM fallback offers
// (APCM_FALLBACK_ELIGIBLE excludes PCM codes). Mirrors CCM_TIERS shape.
const PCM_TIERS = {
  '99426': {
    name: 'Principal Care Management — Clinical Staff (99426)',
    tier: 'PCM',
    enrollment: 'Patient enrolled in Principal Care Management (PCM) for a single complex chronic condition expected to last at least 3 months and placing the patient at significant risk of hospitalization, acute exacerbation/decompensation, functional decline, or death. Care management services are provided by clinical staff under the direction of the billing practitioner, following a disease-specific care plan established, implemented, revised, or monitored for this condition.',
    complianceExtra: ['PCM services this month addressed the single principal condition under a disease-specific care plan; 30 minutes of clinical staff time directed by the billing practitioner were furnished.']
  },
  '99424': {
    name: 'Principal Care Management — Practitioner (99424)',
    tier: 'Practitioner PCM',
    enrollment: 'Patient enrolled in Principal Care Management (PCM) for a single complex chronic condition expected to last at least 3 months and placing the patient at significant risk of hospitalization, acute exacerbation/decompensation, functional decline, or death. Care management services this month were provided personally by the physician or other qualified health care professional, following a disease-specific care plan established, implemented, revised, or monitored for this condition.',
    complianceExtra: ['PCM services this month were personally furnished by the billing practitioner and addressed the single principal condition under a disease-specific care plan; the 30-minute monthly threshold was met.']
  }
};
// PCM_BASE_CODES + pcmPrincipalCondition moved to lib/pcm-principal.js —
// shared with careplan-generate.js (disease-specific template selection) and
// onboard.jsx (enrollment-time condition capture) so all three agree on
// which condition a PCM enrollment manages.

// v2 source: BN_COMBINED_KEY (ccm-compliance.js:6643)
const BN_COMBINED_KEY = 'bn_combined';

// ── Helpers ──────────────────────────────────────────────────────────────────

// v2 source: conditionsToString (ccm-compliance.js:25-28)
function conditionsToString(conditions) {
  if (typeof conditions === 'string') return conditions;
  return (conditions || []).map(function(c) { return c.description; }).join('; ');
}

// v2 source: ccmChronicConditionCount (ccm-compliance.js:31-37)
function ccmChronicConditionCount(patient) {
  if (!patient || !Array.isArray(patient.conditions)) return 0;
  return patient.conditions.filter(function(c) {
    const desc = typeof c === 'string' ? c : (c && (c.description || c.conditionId)) || '';
    return String(desc).trim().length > 0;
  }).length;
}

// v2 source: formatCpMonth (ccm-compliance.js:5762-5766)
const _cpMonthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function formatCpMonth(ym) {
  const parts = ym.split('-');
  const mi = parseInt(parts[1], 10) - 1;
  return _cpMonthNames[mi] + ' ' + parts[0];
}

// v2 source: bcpInterpolate (ccm-compliance.js:12961-12966)
function bcpInterpolate(template, vars) {
  if (!template) return '';
  return template.replace(/\{\{(\w+)\}\}/g, function(_, key) {
    return vars && vars[key] != null ? String(vars[key]) : '';
  });
}

// Template-AGNOSTIC no-assessment override directive. The BHI billing-note
// template can be overridden per-tenant (settings.bulk_bhi_bn_template), and an
// old custom template could still instruct the model to assert a rating-scale
// assessment. To keep the "no false assessment" guarantee independent of the
// template source, this high-priority directive is APPENDED to the FINAL
// assembled prompt (after interpolation) in the no-assessment override case —
// so it lands last and overrides any earlier scale-referencing instruction,
// whether from the default template or a custom one.
const BHI_NO_ASSESSMENT_OVERRIDE_DIRECTIVE =
  'OVERRIDE — No validated rating-scale assessment is on file for this service period. '
  + 'Do NOT reference, cite, or assert any rating-scale assessment, score, severity, or result '
  + 'anywhere in this note, regardless of any instruction above.';

// Append the override directive to an already-interpolated prompt when the
// no-assessment override is in effect. `noAssessmentOverride` is true only when
// the operator explicitly overrode AND no assessment is actually on file (the
// gate surfaced the 'Initial assessment missing' warning). Otherwise the prompt
// is returned unchanged.
function bnApplyNoAssessmentOverrideDirective(prompt, noAssessmentOverride) {
  if (!noAssessmentOverride) return prompt;
  return String(prompt || '') + '\n\n' + BHI_NO_ASSESSMENT_OVERRIDE_DIRECTIVE;
}

// v2 source: getMonthlyActivitySummary (ccm-compliance.js:6289-6314).
// Reads from the patient blob (passed in) rather than the global getPatient().
//
// `programFilter` is optional ('ccm' | 'bhi'). When set, entries are filtered
// by `entryProgramFamily` so BHI time doesn't leak into CCM totals (or vice
// versa). Legacy untagged entries are treated as 'ccm' by
// `entryProgramFamily` (single source of truth in bhi-billing.js). When
// omitted, every non-consolidated entry in the month is returned — useful
// for callers that need a cross-program view (e.g. push-emr's per-patient
// minute total before program eligibility is known).
function getMonthlyActivitySummary(patient, monthStr, programFilter) {
  const p = patient;
  if (!p || !Array.isArray(p.activityLog)) return { total: 0, byType: {}, byTypeNotes: {}, entries: [] };
  const prefix = monthStr;
  const entries = p.activityLog.filter(function(e) {
    if ((e.date || '').indexOf(prefix) !== 0) return false;
    if (e.isConsolidatedNote) return false;
    if (programFilter && entryProgramFamily(e) !== programFilter) return false;
    return true;
  });
  const byType = {};
  const byTypeNotes = {};
  let total = 0;
  entries.forEach(function(e) {
    const dur = parseInt(e.duration) || 0;
    const type = entryActivityType(e);
    byType[type] = (byType[type] || 0) + dur;
    total += dur;
    const note = entryActivityNote(e);
    if (note) {
      if (!byTypeNotes[type]) byTypeNotes[type] = [];
      byTypeNotes[type].push(note);
    }
  });
  return { total, byType, byTypeNotes, entries };
}

function entryActivityType(entry) {
  const type = String((entry && (entry.type || entry.label || entry.desc)) || '').trim();
  return type || 'Other';
}

function entryActivityNote(entry) {
  const e = entry || {};
  const note = String(e.notes || e.description || e.desc || e.hint || '').trim();
  if (!note) return '';
  const type = entryActivityType(e);
  const label = String(e.label || '').trim();
  if (note === type || note === label) return '';
  return note;
}

// v2 source: getCcmBillingCodes (ccm-compliance.js:6317-6339)
// `opts.monthStr` (4th arg) enables same-bill family pairings. Callers that need
// pairings must pass `{ monthStr }`; base-only callers can omit the 4th arg.
// opts.painScaleOverride is accepted only after the Studio soft guard records
// an explicit acknowledgement.
function getCcmBillingCodes(patient, totalMinutes, overrideBaseCode, opts) {
  const b = (patient && patient.billing) || {};
  const baseCode = overrideBaseCode || b.program;
  if (!baseCode || baseCode === 'none') return [];
  // APCM codes (G0556/G0557/G0558) and PCM base codes (99424/99426) aren't in
  // CCM_TIERS but live in DEFAULT_RATES/BILLING_TIME_TARGETS (PCM add-ons
  // 99425/99427 resolve through ADDON_CONFIG like the CCM family's).
  const isApcm = baseCode === 'G0556' || baseCode === 'G0557' || baseCode === 'G0558';
  if (!isApcm && !CCM_TIERS[baseCode] && !PCM_BASE_CODES.has(baseCode)) return [];
  const target = BILLING_TIME_TARGETS[baseCode] != null ? BILLING_TIME_TARGETS[baseCode] : 20;
  const rateInfo = DEFAULT_RATES[baseCode] || {};
  const codes = [{ code: baseCode, desc: rateInfo.desc || baseCode, target, met: totalMinutes >= target }];

  // Chronic Pain Management pairing — G3002 (+G3003 units) stacks on CCM
  // tiers AND APCM bases with distinct pain-family time; deliberately NOT on
  // PCM (unverified concurrency). Same shape as the APCM+G0570 fold below:
  // gates pass → append; enrolled-but-blocked → audit the drop so a missed
  // pairing is queryable instead of silent.
  const painEligibleBase = isApcm || !!CCM_TIERS[baseCode];
  function _appendPainPairing() {
    const monthStr = opts && opts.monthStr;
    if (!monthStr || !painEligibleBase || !isPainEnrolledPatient(patient)) return;
    const gates = painGatesForMonth(patient, monthStr, opts && opts.staffList,
      opts && { scaleOverride: opts.painScaleOverride === true });
    if (gates.ok) {
      resolvePainCodes(patient, monthStr, { G3002: (DEFAULT_RATES['G3002'] || {}).desc, G3003: (DEFAULT_RATES['G3003'] || {}).desc }, opts && opts.staffList)
        .forEach(function(c) { codes.push(c); });
    } else if (opts && opts.auditOnDrop && patient && patient.id) {
      const detail = `G3002 not paired for ${monthStr}; blockers: ${(gates.blockers || []).join('; ')}`;
      safeAudit('PAIN_G3002_DROPPED', patient.id, detail).catch(() => {});
    }
  }
  const pinEligibleBase = isApcm || !!CCM_TIERS[baseCode];
  function _appendPinPairing() {
    const monthStr = opts && opts.monthStr;
    if (!monthStr || !pinEligibleBase || !isPinEnrolledPatient(patient)) return;
    const gates = pinGatesForMonth(patient, monthStr);
    if (gates.ok) {
      resolvePinCodes(patient, monthStr, { G0023: (DEFAULT_RATES['G0023'] || {}).desc, G0024: (DEFAULT_RATES['G0024'] || {}).desc })
        .forEach(function(c) { codes.push(c); });
    } else if (opts && opts.auditOnDrop && patient && patient.id) {
      const detail = `G0023 not paired for ${monthStr}; blockers: ${(gates.blockers || []).join('; ')}`;
      safeAudit('PIN_G0023_DROPPED', patient.id, detail).catch(() => {});
    }
  }

  // APCM has no add-on configuration — only CCM tiers do. Skip ADDON_CONFIG
  // lookup for G-codes to avoid surprises. APCM CAN pair with G0570 (BHI
  // add-on) when the patient is BHI-enrolled and BHI gates pass for the month.
  if (isApcm) {
    const monthStr = opts && opts.monthStr;
    if (monthStr) {
      const gates = bhiGatesForCode(patient, monthStr, 'G0570');
      if (gates.ok) {
        const g0570Info = DEFAULT_RATES['G0570'] || {};
        codes.push({ code: 'G0570', unit: 1, desc: g0570Info.desc || 'G0570', target: 0, met: true });
      } else if (opts && opts.auditOnDrop && b.bhiProgram && patient && patient.id) {
        const blockersStr = (gates.blockers || []).join('; ');
        const detail = `G0570 not paired with APCM for ${monthStr}; blockers: ${blockersStr}`;
        safeAudit('BHI_G0570_DROPPED', patient.id, detail).catch(() => {});
      }
    }
    _appendPinPairing();
    _appendPainPairing();
    return codes;
  }

  let cfg = null;
  for (let i = 0; i < ADDON_CONFIG.length; i++) {
    if (ADDON_CONFIG[i].base === baseCode) { cfg = ADDON_CONFIG[i]; break; }
  }
  if (cfg && totalMinutes > target) {
    const overflow = totalMinutes - target;
    const perUnit = BILLING_TIME_TARGETS[cfg.addonCode] || target;
    const units = Math.min(Math.floor(overflow / perUnit), cfg.max);
    for (let u = 1; u <= units; u++) {
      const aoInfo = DEFAULT_RATES[cfg.addonCode] || {};
      codes.push({ code: cfg.addonCode, unit: u, desc: aoInfo.desc || cfg.addonCode, target: perUnit, met: true });
    }
  }
  _appendPinPairing();
  _appendPainPairing();
  return codes;
}

// ── Read-only reimbursement helpers (display-only; no billing/eligibility
// logic) ─────────────────────────────────────────────────────────────────────
// Rates are 2026 CMS MPFS Non-Facility, MAC Locality 1320202 (Long Island, NY)
// — see DEFAULT_RATES above / ccm-compliance.js:7834. Exported so the CCM
// tier-bump review can show, as NEUTRAL information, which affirmable tier pays
// most at a given minute count. Callers MUST NOT use this to auto-select a tier.
export const RATE_LOCALITY_LABEL = 'CMS 2026, Long Island NY (MAC 1320202)';

// Total allowed reimbursement for a single CCM base code at `minutes`, summing
// the base unit + any add-on units the existing add-on split awards. Reuses
// getCcmBillingCodes + DEFAULT_RATES so the numbers are never duplicated.
// Codes whose time target is NOT met (met: false) are excluded — an unmet base
// code isn't billable at that minute count, so ranking it by its full rate
// would surface an unbillable tier as "highest". At/above target every code is
// met, so callers ranking rows >= target minutes see identical numbers.
export function ccmTierReimbursement(baseCode, minutes) {
  const mins = Number(minutes) || 0;
  // Minimal synthetic patient — getCcmBillingCodes reads only billing.program
  // for CCM codes, and we pass the override anyway.
  const codes = getCcmBillingCodes({ billing: { program: baseCode } }, mins, baseCode);
  let total = 0;
  for (const c of codes) {
    if (c.met === false) continue; // unmet target -> not billable -> $0 here
    const info = DEFAULT_RATES[c.code];
    if (info) total += info.rate;
  }
  return total;
}

// Rank the three affirmable CCM tiers by reimbursement at `minutes`. Returns
// [{ code, dollars }] sorted high→low. Display-only: the caller marks the
// highest-paying tier as neutral information and never pre-selects it.
export function rankCcmTiersByReimbursement(minutes, codes) {
  const list = (codes && codes.length ? codes : ['99487', '99491', '99490']);
  return list
    .map((code) => ({ code, dollars: ccmTierReimbursement(code, minutes) }))
    .sort((a, b) => b.dollars - a.dollars);
}

// v2 source: bnGetTpl (ccm-compliance.js:13070-13073). Settings loader
// inlined; the cache key (bulk_bn_template) is preserved.
let _bnTplCache = null;
let _bnTplLoaded = false;
let _bnTplInflight = null;
async function loadBnTemplate() {
  if (_bnTplLoaded) return;
  if (_bnTplInflight) return _bnTplInflight;
  _bnTplInflight = (async () => {
    try {
      const s = await ccmApi.getSettings();
      if (s && s.bulk_bn_template != null) _bnTplCache = s.bulk_bn_template || null;
    } catch (_) {}
    _bnTplLoaded = true;
    _bnTplInflight = null;
  })();
  return _bnTplInflight;
}
function bnGetTpl() {
  if (_bnTplCache != null && String(_bnTplCache).trim()) return _bnTplCache;
  return BN_DEFAULT;
}

// v2 source: bnBuildSectionInstructions (ccm-compliance.js:13094-13121)
// `hasAssessment` (BHI 99484 only): when false (no rating-scale assessment on
// file — the override case) the GOALS/INTERVENTIONS instructions carry NO
// rating-scale/score/PHQ/GAD wording, so the assembled no-assessment GOALS
// section is scale-free. Defaults to false (conservative) for callers that
// don't pass it; CCM tiers ignore it entirely.
function bnBuildSectionInstructions(tierCode, hasAssessment) {
  const isApcm = tierCode === 'G0556' || tierCode === 'G0557' || tierCode === 'G0558';
  if (isApcm) {
    return 'APCM billing notes are assembled from structured audit attestations. Do not write GOALS, INTERVENTIONS, time summaries, no-activity language, payment-policy explanations, or a chronic-condition essay.';
  }
  // BHI 99484 — separate audit voice. References the rating-scale assessment
  // and the BH dx already interpolated into the prompt. No minutes per bullet
  // (itemized log renders separately downstream).
  const isBhi = tierCode === '99484';
  if (isBhi) {
    let s = 'Write TWO sections in plain text with bullet points. Use third-person physician-charting voice. Do NOT use first-person ("I", "my", "we"):\n\n';
    s += '1. GOALS (3-5 bullet points):\n';
    if (hasAssessment) {
      s += '   Patient-tailored but audit-safe goals anchored to the behavioral health condition listed above. Reference the rating-scale assessment ONLY as permitted by the rating-scale line and rule in the prompt: when a score is on file, reflect it verbatim without inventing additional clinical findings. Examples for depression: "Continued treatment for [PATIENT_NAME]\'s major depressive disorder with goal of symptom reduction.", "Medication adherence supported as documented.", "Sleep and behavioral activation strategies discussed when clinically appropriate." Do NOT create medication-change statements, treatment-response claims, or therapy-modality decisions unless those exact facts appear in the supplied activity log.\n\n';
      s += '2. INTERVENTIONS (1-4 thematic bullets — synthesize the month\'s behavioral health care management work):\n';
      s += '   Group the logged BHI activities into conservative audit themes (rating-scale administration, behavioral interventions discussed, care coordination with mental-health specialists, medication-adherence support, caregiver communication). Themes must be substantiated by entries in the activity log. Do NOT mention "minutes documented" or per-entry durations — the itemized time log is rendered separately. Do NOT write per-entry dates ("On YYYY-MM-DD..."). Do NOT write a bullet per logged entry. Use note-form or passive constructions.\n';
    } else {
      // No assessment on file: zero rating-scale / score / PHQ / GAD wording.
      s += '   Patient-tailored but audit-safe goals anchored to the behavioral health condition listed above, focused on symptom monitoring, functional status, and treatment adherence. Examples for depression: "Continued treatment for [PATIENT_NAME]\'s major depressive disorder with goal of symptom reduction.", "Medication adherence supported as documented.", "Sleep and behavioral activation strategies discussed when clinically appropriate." Do NOT create medication-change statements, treatment-response claims, or therapy-modality decisions unless those exact facts appear in the supplied activity log.\n\n';
      s += '2. INTERVENTIONS (1-4 thematic bullets — synthesize the month\'s behavioral health care management work):\n';
      s += '   Group the logged BHI activities into conservative audit themes (behavioral interventions discussed, care coordination with mental-health specialists, medication-adherence support, caregiver communication). Themes must be substantiated by entries in the activity log. Do NOT mention "minutes documented" or per-entry durations — the itemized time log is rendered separately. Do NOT write per-entry dates ("On YYYY-MM-DD..."). Do NOT write a bullet per logged entry. Use note-form or passive constructions.\n';
    }
    return s;
  }
  // PCM — same charting voice and guardrails as the CCM sections, but every
  // anchor is the SINGLE principal condition and its disease-specific plan.
  const isPcm = tierCode === '99424' || tierCode === '99426';
  if (isPcm) {
    let s = 'Write TWO sections in plain text with bullet points. Use third-person physician-charting voice throughout — note-form for practitioner actions and [PATIENT_NAME] as subject for patient-centered statements. Do NOT use first-person ("I", "my", "we"). This is a Principal Care Management (PCM) note: anchor EVERY statement to the single principal condition named in the enrollment line above — do NOT write goals or interventions for other conditions on the problem list:\n\n';
    s += '1. GOALS (2-4 bullet points):\n';
    s += '   Patient-tailored but minimum audit-safe goals for the principal condition only, using condition-native management aims (e.g., "Heart-failure management focused on symptom stability, medication adherence, and prevention of hospitalization."). Do NOT create numeric targets, medication-continuation statements, disease-control status, specialist involvement, treatment changes, or outcomes unless those exact facts appear in the supplied patient data or itemized activity log. Include a patient-centered goal ONLY if one is supplied.\n\n';
    s += '2. INTERVENTIONS (1-3 thematic bullets — synthesize the month\'s work on the principal condition, do NOT recap entries one-by-one):\n';
    s += '   Group the logged activities into conservative audit themes (disease-specific care-plan monitoring, medication adherence support for the principal condition, services/referral coordination, record review). Themes must be substantiated by entries in the itemized activity log above — do NOT invent work. Do NOT restate durations or per-entry dates; the itemized time log renders separately. Do NOT write generic filler.\n';
    if (tierCode === '99424') {
      s += '   Include this exact bullet: "- Principal Care Management services this month personally furnished by the billing practitioner."\n';
    }
    s += '   Include one bullet documenting the disease-specific plan review. Format: "Disease-specific care plan for the principal condition reviewed this month; [specific change documented in the chart]" — or, if no change is documented, exactly: "Disease-specific care plan for the principal condition reviewed this month; no material changes indicated."\n';
    return s;
  }
  const isComplex = tierCode === '99487';
  const isPhysLed = tierCode === '99491';
  const sectionCount = isComplex ? 'THREE' : 'TWO';
  let s = 'Write ' + sectionCount + ' sections in plain text with bullet points. Use third-person physician-charting voice throughout — note-form for practitioner actions ("Care plan monitored.", "Medication adherence supported.", "Coordinated services/referrals.") and [PATIENT_NAME] as subject for patient-centered statements. Do NOT use first-person ("I", "my", "we"):\n\n';
  s += '1. GOALS (3-5 bullet points):\n';
  s += '   Write patient-tailored but minimum audit-safe goals anchored to the listed chronic conditions. Use condition-native management aims by default, e.g., "Asthma management focused on symptom control and prevention of exacerbations.", "Epilepsy management focused on seizure-risk reduction and medication adherence support.", "Hyperlipidemia management focused on cardiovascular risk reduction.", and "GERD management focused on symptom control and prevention of complications."\n';
  s += '   Do NOT create numeric targets, medication-continuation statements, disease-control status, specialist involvement, treatment changes, or outcomes unless those exact facts appear in the supplied patient data or itemized activity log. Include a patient-centered goal ONLY if one is supplied (a patient-stated preference, adherence concern, or patient-set target). If none is supplied, do not invent one.\n\n';
  s += '2. INTERVENTIONS (1-4 thematic bullets — synthesize the month\'s coordination work, do NOT recap entries one-by-one):\n';
  s += '   Group the logged activities into conservative audit themes (services/referral coordination, medication adherence support, caregiver communication, record review, care-plan monitoring) and write one bullet per substantive theme. Themes must be substantiated by entries in the itemized activity log above — do NOT invent work or themes the log does not support. When entry notes contain specifics (drug names, doses, lab values, decisions), incorporate them only if directly stated; when notes are empty, acknowledge scope without padding (e.g., "Three care coordination touchpoints supported continuity across [PATIENT_NAME]\'s listed chronic conditions this month."). Do NOT mention limitations of the source data, entry notes, documentation detail, or lack of specifics. Do NOT restate durations or per-entry activity types — those appear in the itemized time log section below this AI-generated section, so duplicating them adds noise. Do NOT write per-entry dates in the prose ("On YYYY-MM-DD ..."); the Service Period header anchors the timeframe and per-entry dates are deliberately omitted from the rendered note body. Do NOT write a bullet per logged entry. Do NOT write generic filler like "Reviewed clinical status." or "Patient outreach completed." unless those phrases trace to entry notes. Use note-form or passive constructions; no first-person.\n';
  if (isPhysLed) {
    s += '   Include this exact bullet: "- Care management plan personally reviewed and directed by the billing practitioner this month."\n';
  } else if (!isComplex) {
    s += '   Include one bullet documenting plan review. Format: "Care plan reviewed this month; [specific change documented in the chart]" — or, if no change is documented, exactly: "Care plan reviewed this month; no material changes indicated."\n';
  }
  if (isComplex) {
    s += '\n3. CARE PLAN CHANGES THIS MONTH (2-4 bullet points):\n';
    s += '   Substantive revisions or active management decisions made during this billing period, drawn ONLY from documented evidence in the activity log notes. Examples only when explicitly documented: "Medication adherence plan updated for [condition].", "Monitoring interval changed as documented.", "Referral coordination completed for [documented specialty/service]."\n';
    s += '   If no substantive revision is documented in the activity log notes, write exactly: "- No substantive care-plan revision is documented in this month\'s activity log; by signing, the billing practitioner attests the care plan was established or substantially revised as 99487 requires (or directs tier correction before billing)."\n\n';
  }
  return s;
}

// v2 source: bnBuildPromptVars (ccm-compliance.js:13125-13156)
// `monthStr` (4th arg) is BHI-only — needed to interpolate {{service_period}}
// into the BHI prompt template. CCM/APCM callers pass null/undefined and the
// existing return path is unchanged.
function bnBuildPromptVars(patient, activitySummary, tierCode, monthStr) {
  // PCM prompts see ONLY the principal condition — interpolating the full
  // problem list would invite goals/interventions for conditions the PCM
  // enrollment doesn't manage. Resolution is gated upstream (generation
  // throws when the principal condition is ambiguous), so this is non-null
  // for every PCM tierCode that reaches prompt assembly.
  const isPcmTier = PCM_BASE_CODES.has(tierCode);
  const pcmPrincipal = isPcmTier ? pcmPrincipalCondition(patient) : null;
  // Chart free text is tag-neutralized before prompt interpolation
  // (prompt-text.js) — prompt vars only; the deterministic note builders
  // below interpolate the same fields into DOCUMENT text and stay verbatim.
  const conditions = promptSafeLine(isPcmTier
    ? conditionsToString(pcmPrincipal ? [pcmPrincipal] : patient.conditions)
    : conditionsToString(patient.conditions)) || 'Not specified';
  // Accept either a pre-formatted string (v2 callers) or the structured
  // medications array (v3 callers). For the array case, dedupe by drug
  // identity so the same drug at multiple strengths shows up once at the
  // most recently prescribed strength. See medication-dedupe.js.
  const medications = promptSafeLine(typeof patient.medications === 'string'
    ? patient.medications
    : medicationsArrayToPromptString(dedupeMedicationsByDrug(patient.medications)));
  const allergies = promptSafeLine(typeof patient.allergies === 'string' ? patient.allergies : '');
  const sexLabel = patient.sex === 'F' ? 'Female' : patient.sex === 'M' ? 'Male' : '';
  const activityTypes = promptSafeLine(activitySummary && activitySummary.byType ? Object.keys(activitySummary.byType).join(', ') : '') || '(none logged)';
  const entries = (activitySummary && Array.isArray(activitySummary.entries)) ? activitySummary.entries : [];
  const sortedEntries = entries.slice().sort(function(a, b) { return (a.date || '').localeCompare(b.date || ''); });
  const isApcm = !!APCM_TIERS[tierCode];
  const isBhi = tierCode === '99484';
  const activityLog = sortedEntries.length
    ? sortedEntries.map(function(e) {
        const dur = parseInt(e.duration) || 0;
        const activityNote = promptSafeLine(entryActivityNote(e), 200);
        const note = activityNote ? ' — ' + activityNote : '';
        return '- ' + dur + ' min — ' + promptSafeLine(entryActivityType(e), 80) + note;
      }).join('\n')
    : isApcm
      ? '(APCM billing note is assembled from structured audit fields; no AI-generated activity prose is used.)'
    : isBhi
      ? '(NO BHI ACTIVITY LOGGED THIS MONTH. Output exactly one INTERVENTIONS bullet and no others: "- No BHI activity documented in the activity log for this billing period." Do NOT infer work from the patient\'s conditions.)'
    : '(NO ACTIVITY LOGGED THIS MONTH. The billing practitioner has zero documented CCM activity entries. For the INTERVENTIONS section, output exactly one bullet and no others: "- No CCM activity documented in the activity log for this billing period." Do NOT write generic care-coordination bullets. Do NOT write a plan-review bullet. Do NOT infer work from the patient\'s conditions.)';

  // BHI 99484 prompt vars — distinct shape from CCM/APCM. References the BH
  // dx, latest rating-scale assessment, and service period.
  if (isBhi) {
    // Latest BY DATE through the service month — same helper the deterministic
    // RATING SCALE block uses, so AI prose and the printed block can't cite
    // different scores or future assessments in one signed 99484 note.
    // Latest assessment ON/BEFORE the service month end. May be null under the
    // assessment-override soft guard (no rating scale on file for the period).
    const latestAssessment = _latestAssessmentForMonth(patient && patient.bhi && patient.bhi.assessments, monthStr);
    const bhiDx = promptSafeLine((patient && patient.bhi && patient.bhi.dx) || conditionsToString(patient.conditions)) || 'Behavioral health condition (unspecified)';
    let servicePeriod = '';
    if (monthStr) {
      const yy = parseInt(monthStr.split('-')[0], 10);
      const mm = parseInt(monthStr.split('-')[1], 10);
      const lastDay = new Date(yy, mm, 0).getDate();
      servicePeriod = monthStr + '-01 to ' + monthStr + '-' + lastDay;
    }
    // Deterministic rating-scale block for the prompt body. When an assessment
    // is on file we hand the model the scale/score/date to reference verbatim.
    // When NONE is on file (override case) we must NOT feed a placeholder that
    // the model can render as a claim — instead we state the absence and
    // instruct the model to omit any rating-scale reference. This mirrors the
    // deterministic attestation softening in assembleBhiBillingNote.
    let ratingScaleBlock;
    let ratingScaleRule;
    let ratingScaleScoreRule; // fills rule 9 — must NOT assert a score on file when none exists
    if (latestAssessment) {
      const sev = latestAssessment.severity ? ' (' + promptSafeLine(latestAssessment.severity, 40) + ')' : '';
      ratingScaleBlock = 'Latest rating-scale assessment: ' + promptSafeLine(latestAssessment.scale || '', 60)
        + ' score ' + (latestAssessment.score != null ? promptSafeLine(latestAssessment.score, 12) : '')
        + sev + ' on ' + promptSafeLine(latestAssessment.date || '', 20) + '.';
      ratingScaleRule = 'A validated rating-scale assessment IS on file (above). Reference it verbatim; do not alter the score or severity.';
      ratingScaleScoreRule = 'The rating-scale score is the score on file — interpolate it verbatim. Do not infer severity beyond what the structured assessment record states, and do not synthesize clinical impressions from the score.';
    } else {
      ratingScaleBlock = 'Latest rating-scale assessment: NONE on file for this service period.';
      ratingScaleRule = 'NO validated rating-scale assessment is on file for this period. Do NOT reference, cite, or assert any rating-scale assessment, score, or severity anywhere in the note. Do not invent one. Document only the behavioral-health care-management work in the activity log.';
      // No score is on file — rule 9 must NOT claim one exists (that would
      // contradict rule 10). Reinforce the no-reference stance instead.
      ratingScaleScoreRule = 'No rating-scale score is on file for this period; do NOT state or imply any score, trend, or severity.';
    }
    // Conditional GOALS examples (fills {{goal_examples}}). When an assessment
    // is on file, keep the condition-native examples (incl. scale-adjacent
    // "symptom-trend tracking"). When ABSENT, use neutral measurable-goal
    // examples anchored to symptom frequency / functional status / adherence —
    // NO rating-scale, PHQ, or GAD reference — so the no-assessment GOALS
    // section carries zero scale language. Same pattern as rule 9 / the voice
    // example. The scale-specific target parenthetical and the "if a score is
    // supplied" clause are likewise dropped when absent.
    const hasAssessment = !!latestAssessment;
    let goalExamples;
    let goalScaleTargetParen;
    let goalScaleSuppliedClause;
    if (hasAssessment) {
      goalExamples = [
        '- "Major depressive disorder management focused on symptom monitoring and treatment adherence."',
        '- "Generalized anxiety disorder management focused on symptom-trend tracking and coping support."',
        '- "PTSD management focused on safety, symptom monitoring, and care coordination with treating clinician."',
      ].join('\n');
      goalScaleTargetParen = ' (PHQ-9 below X, GAD-7 below Y, etc.)';
      goalScaleSuppliedClause = ' If a rating-scale score, medication plan, or status is supplied, use it conservatively and verbatim without adding extra clinical interpretation.';
    } else {
      goalExamples = [
        '- "Major depressive disorder management focused on symptom monitoring and treatment adherence."',
        '- "Generalized anxiety disorder management focused on functional-status support and coping strategies."',
        '- "PTSD management focused on safety, symptom-frequency monitoring, and care coordination with treating clinician."',
      ].join('\n');
      goalScaleTargetParen = '';
      goalScaleSuppliedClause = ' If a medication plan or status is supplied, use it conservatively and verbatim without adding extra clinical interpretation.';
    }
    return {
      bhi_dx: bhiDx,
      sex_line: sexLabel ? 'Sex at birth: ' + sexLabel + '\n' : '',
      medications_line: medications ? 'Medications: ' + medications + '\n' : '',
      allergies_line: allergies ? 'Allergies: ' + allergies + '\n' : '',
      rating_scale_block: ratingScaleBlock,
      rating_scale_rule: ratingScaleRule,
      rating_scale_score_rule: ratingScaleScoreRule,
      // Legacy-compat: pre-existing custom bulk_bhi_bn_template templates may
      // still reference {{scale_name}}/{{scale_score}}/{{scale_severity}}/
      // {{scale_date}} (replaced by rating_scale_block etc.). Keep emitting
      // them with the old empty-value fallbacks so those templates don't
      // silently render blank (bcpInterpolate substitutes '' for missing vars).
      scale_name: promptSafeLine(latestAssessment && latestAssessment.scale, 60) || '(no scale on file)',
      scale_score: (latestAssessment && latestAssessment.score != null) ? promptSafeLine(latestAssessment.score, 12) : '(no score)',
      scale_severity: promptSafeLine(latestAssessment && latestAssessment.severity, 40) || '',
      scale_date: promptSafeLine(latestAssessment && latestAssessment.date, 20) || '(no date)',
      goal_examples: goalExamples,
      goal_scale_target_paren: goalScaleTargetParen,
      goal_scale_supplied_clause: goalScaleSuppliedClause,
      service_period: servicePeriod,
      activity_log: activityLog,
      section_instructions: bnBuildSectionInstructions(tierCode, hasAssessment),
    };
  }

  const tier = CCM_TIERS[tierCode] || APCM_TIERS[tierCode] || PCM_TIERS[tierCode] || CCM_TIERS['99490'] || { tier: 'CCM', name: 'CCM' };
  return {
    tier_name: tier.tier,
    conditions,
    sex_line: sexLabel ? 'Sex at birth: ' + sexLabel + '\n' : '',
    medications_line: medications ? 'Medications: ' + medications + '\n' : '',
    allergies_line: allergies ? 'Allergies: ' + allergies + '\n' : '',
    activity_types: activityTypes,
    activity_log: activityLog,
    section_instructions: bnBuildSectionInstructions(tierCode),
  };
}

// v2 source: buildBillingNoteAIPrompt (ccm-compliance.js:6407-6409).
// `monthStr` forwarded to bnBuildPromptVars for the BHI 99484 path (service
// period interpolation). CCM/APCM ignore it.
function buildBillingNoteAIPrompt(patient, activitySummary, tierCode, monthStr) {
  return bcpInterpolate(bnGetTpl(), bnBuildPromptVars(patient, activitySummary, tierCode, monthStr));
}

// Caching variant: split the billing-note prompt at the <patient_data> block
// into a { cachedPrefix, dynamicSuffix } pair. The head (role/voice/rules/task)
// carries only tier-level placeholders ({{tier_name}}, {{section_instructions}}),
// so its interpolation is byte-identical for every patient on a tier — a stable
// cache key. All patient-volatile placeholders live in the trailing block.
// Reconstruction invariant: cachedPrefix + dynamicSuffix === buildBillingNoteAIPrompt(...).
// Falls back to a non-cached single string (cachedPrefix '') if the template has
// no <patient_data> marker, or its head carries a patient-volatile placeholder
// (e.g. a customized template) — either of which would make the prefix unstable
// or wrong to cache.
function buildBillingNoteAIPromptCached(patient, activitySummary, tierCode, monthStr) {
  const tpl = bnGetTpl();
  const vars = bnBuildPromptVars(patient, activitySummary, tierCode, monthStr);
  const marker = '<patient_data>';
  const idx = tpl.indexOf(marker);
  const head = idx >= 0 ? tpl.slice(0, idx) : '';
  // rating_scale_block / rating_scale_rule are patient-volatile (they change
  // per-patient with assessment presence, incl. the override case), so they
  // must disable prefix caching if they ever land in a template's head — same
  // as the other patient-volatile tokens. (They supersede the scale_* set,
  // which is still emitted for legacy custom-template compat.)
  const patientVarsRe = /\{\{(conditions|sex_line|medications_line|allergies_line|activity_types|activity_log|bhi_dx|rating_scale_block|rating_scale_rule|rating_scale_score_rule|scale_name|scale_score|scale_severity|scale_date|goal_examples|goal_scale_target_paren|goal_scale_supplied_clause|service_period|patientData|priorPlan)\}\}/;
  if (idx < 0 || patientVarsRe.test(head)) {
    return { cachedPrefix: '', dynamicSuffix: bcpInterpolate(tpl, vars) };
  }
  return {
    cachedPrefix: bcpInterpolate(head, vars),
    dynamicSuffix: bcpInterpolate(tpl.slice(idx), vars),
  };
}

// v2 source: _resolveStaffCredential (ccm-compliance.js:10586-10592)
function _resolveStaffCredential(staffName, staffList) {
  if (!staffName) return 'none';
  const target = String(staffName).toLowerCase();
  const list = Array.isArray(staffList) ? staffList : [];
  const match = list.find(function(s) { return String(s.name || '').toLowerCase() === target; });
  if (match && match.credential && STAFF_CREDENTIALS.indexOf(match.credential) !== -1) return match.credential;
  return 'none';
}

// v2 source: _formatStaffWithCredential (ccm-compliance.js:10595-10600)
function _formatStaffWithCredential(staffName, credential) {
  const n = String(staffName || '').trim();
  if (!n) return '';
  const c = credential && STAFF_CREDENTIALS.indexOf(credential) !== -1 && credential !== 'none' ? credential : '';
  return c ? n + ', ' + c : n;
}

function _formatEntryStaff(entry, staffList) {
  const staffName = String((entry && (entry.staff || entry.staffName || entry.createdByName || entry.authorName)) || '').trim();
  if (!staffName) return 'Clinical staff';
  const cred = entry.credential || _resolveStaffCredential(staffName, staffList);
  return _formatStaffWithCredential(staffName, cred);
}

// v2 source: getBillingNoteCarePlan (ccm-compliance.js:5724-5732). Takes the
// _cpHistory cache instead of reading a global.
function getBillingNoteCarePlan(cpHistory, patientId, monthStr) {
  const h = (cpHistory && cpHistory[patientId]) || {};
  const months = Object.keys(h).filter(function(m) {
    return h[m] && String(h[m]).trim() && (!monthStr || m <= monthStr);
  }).sort();
  if (!months.length) return null;
  const selected = months[months.length - 1];
  return { month: selected, text: h[selected] };
}

function apcmCriteriaLine(code, conditionCount) {
  const conditionLabel = conditionCount + ' chronic ' + (conditionCount === 1 ? 'condition' : 'conditions') + ' documented';
  if (code === 'G0556') return 'Level 1 criteria met: ' + conditionLabel + ' (0-1 required).';
  if (code === 'G0557') return 'Level 2 criteria met: ' + conditionLabel + ' (2 or more required; expected to last at least 12 months or until death).';
  if (code === 'G0558') return 'Level 3 criteria met: QMB eligibility confirmed and ' + conditionLabel + ' (2 or more required; expected to last at least 12 months or until death).';
  return 'APCM criteria met for the selected billing code.';
}

function conditionNativeGoal(condition) {
  const label = String(condition || '').trim().replace(/\s+/g, ' ');
  if (!label) return '';
  const lo = label.toLowerCase();
  if (/asthma/.test(lo)) return label + ' management focused on symptom control and prevention of exacerbations.';
  if (/epilep|seizure/.test(lo)) return label + ' management focused on seizure-risk reduction and medication adherence support.';
  if (/hyperlip|lipid|cholesterol|hypertrig|ldl|hdl/.test(lo)) return label + ' management focused on cardiovascular risk reduction.';
  if (/gerd|gastro.?esophageal reflux|reflux/.test(lo)) return label + ' management focused on symptom control and prevention of complications.';
  if (/alcohol|substance|opioid|dependence|use disorder/.test(lo)) return label + ' management focused on risk reduction, treatment-plan adherence, and prevention of complications.';
  if (/diabetes|\bdm\b|t2dm|type 2/.test(lo)) return label + ' management focused on glycemic stability, medication adherence support, and prevention of complications.';
  if (/heart failure|\bchf\b|cardiomyopathy/.test(lo)) return label + ' management focused on symptom stability and prevention of decompensation.';
  if (/chronic kidney|\bckd\b|renal/.test(lo)) return label + ' management focused on monitoring kidney function, medication safety, and slowing progression.';
  if (/hypertension|\bhtn\b|blood pressure/.test(lo)) return label + ' management focused on blood-pressure control and cardiovascular risk reduction.';
  if (/copd|emphysema/.test(lo)) return label + ' management focused on respiratory symptom control and prevention of exacerbations.';
  if (/hypothy|thyroid/.test(lo)) return label + ' management focused on thyroid replacement adherence and symptom monitoring.';
  if (/osteopenia|osteoporosis/.test(lo)) return label + ' management focused on bone-health monitoring and fracture-risk reduction.';
  if (/pain|arthritis|osteoarthritis/.test(lo)) return label + ' management focused on functional stability, symptom control, and treatment-plan support.';
  if (/cancer|carcinoma|malign|lumpectomy/.test(lo)) return label + ' history monitored under the longitudinal primary care plan with coordination as clinically appropriate.';
  return label + ' management focused on symptom monitoring, treatment-plan support, and prevention of exacerbation, hospitalization, or functional decline.';
}

function buildApcmCarePlanGoals(conditions) {
  const out = [];
  const listed = conditions.slice(0, 5);
  listed.forEach(function(condition) {
    const goal = conditionNativeGoal(condition);
    if (goal) out.push('- ' + goal);
  });
  if (!out.length) {
    out.push('- Longitudinal primary care management focused on access, preventive care, medication review, and coordination based on documented patient needs.');
  } else if (conditions.length > listed.length) {
    out.push('- Additional listed chronic conditions monitored under the comprehensive APCM care plan as clinically appropriate.');
  }
  return out;
}

function buildApcmBillingSummary(patient, codeListInline, tier, apcmDecision) {
  const conditions = conditionsToString(patient.conditions)
    .split(';')
    .map(function(c) { return c.trim(); })
    .filter(Boolean);
  const count = conditions.length;
  const lines = [];
  lines.push('APCM BILLING SUMMARY:');
  lines.push('- ' + codeListInline + ' selected for this billing period: ' + tier.tier + '.');
  lines.push('- ' + apcmCriteriaLine(apcmDecision && apcmDecision.code, count));
  if (conditions.length) {
    lines.push('- Chronic conditions supporting longitudinal primary care management: ' + conditions.join('; ') + '.');
  }
  lines.push('- Comprehensive care plan is on file and supports longitudinal primary care management for this service period.');
  lines.push('');
  lines.push('CARE PLAN GOALS:');
  buildApcmCarePlanGoals(conditions).forEach(function(line) { lines.push(line); });
  return lines;
}

// v2 source: reidentifyText (ccm-compliance.js:3623-3643)
const _KNOWN_SPECIALTY = /^(cardiology|psychiatry|ophthalmology|optometry|neurology|nephrology|urology|gastroenterology|hepatology|endocrinology|oncology|pulmonology|pulmonary|dermatology|hematology|rheumatology|orthopedics?|podiatry|gynecology|obstetrics|allergy|immunology|pain|palliative|primary|internal|family|pediatrics?|geriatrics?|surgery|otolaryngology|ent|sleep|wound|vascular|radiology|pathology|psychology|psychotherapy|counseling|nutrition|dietetics)$/i;
function reidentifyText(text, phiMap) {
  let result = text;
  Object.keys(phiMap).forEach(function(token) {
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(escaped, 'g'), phiMap[token]);
  });
  result = result.replace(/\[SPECIALIST_\d+\](\s+)(\w[\w-]*)/g, function(match, ws, next) {
    return _KNOWN_SPECIALTY.test(next) ? next : 'the specialist' + ws + next;
  });
  result = result.replace(/\[SPECIALIST_\d+\]/g, 'the specialist');
  return result;
}

// v2 source: normalizeAiModel (ccm-compliance.js:3409-3416)
function normalizeAiModel(model) {
  const raw = String(model || '').toLowerCase();
  if (raw === 'bedrock-opus' || raw === 'bedrock-sonnet' || raw === 'bedrock-haiku') return raw;
  if (raw.indexOf('opus') !== -1) return 'bedrock-opus';
  if (raw.indexOf('sonnet') !== -1) return 'bedrock-sonnet';
  if (raw.indexOf('haiku') !== -1 || raw.indexOf('mini') !== -1 || raw.indexOf('nano') !== -1) return 'bedrock-haiku';
  return 'bedrock-sonnet';
}

// v2 source: callAI (ccm-compliance.js:3525-3590)
async function callAI(prompt, model, options) {
  const opts = options || {};
  const modelId = normalizeAiModel(model || 'bedrock-sonnet');
  // When a stable cachedPrefix is supplied, send it as a separate cache-marked
  // content block so Bedrock caches the static template prefix across requests
  // (a bulk run reuses the same tier template for every patient).
  let userContent;
  if (opts.cachedPrefix) {
    userContent = [
      { type: 'text', text: opts.cachedPrefix, cache_control: { type: 'ephemeral' } },
      { type: 'text', text: prompt },
    ];
  } else {
    userContent = prompt;
  }
  const payload = {
    model: modelId,
    max_tokens: opts.max_tokens || 4096,
    temperature: opts.temperature != null ? opts.temperature : 0.3,
    system: opts.system,
    systemCache: opts.systemCache === true,
    messages: [{ role: 'user', content: userContent }],
  };
  const data = await api.post('/ai', payload);
  if (!data.content || !data.content[0] || typeof data.content[0].text !== 'string') {
    throw new Error('AI response was missing expected text content.');
  }
  return data.content[0].text;
}

// v2 source: assembleCombinedBillingNote (ccm-compliance.js:6525-6633).
// staffList passed in (was a global in v2); practiceName passed in
// (was practiceSettings.practiceName globally); cpHistory passed in.
export function assembleCombinedBillingNote(patient, monthStr, aiText, cptCodes, totalMonthTime, activitySummary, staffList, practiceName, cpHistory, apcmDecision, consentType, effectiveCcmCode) {
  const b = patient.billing || {};
  // When an APCM fallback decision exists for this month, the note's tier
  // context, enrollment text, and compliance attestation use APCM language
  // (not the patient's underlying CCM tier). The CPT codes list still
  // reflects what was actually billed — the G-code only.
  const isApcm = !!(apcmDecision && apcmDecision.code && APCM_TIERS[apcmDecision.code]);
  const baseCode = isApcm ? apcmDecision.code : (effectiveCcmCode || b.program);
  const tier = isApcm ? APCM_TIERS[apcmDecision.code] : (CCM_TIERS[baseCode] || PCM_TIERS[baseCode] || CCM_TIERS['99490']);
  const monthLabel = formatCpMonth(monthStr);
  const patientName = [patient.fname, patient.lname].filter(Boolean).join(' ') || '(unknown)';
  const providerName = patient.provider || practiceName || '';

  const codeOrder = [];
  const byCode = {};
  cptCodes.forEach(function(ci) {
    if (!byCode[ci.code]) {
      byCode[ci.code] = { code: ci.code, desc: ci.desc || '', units: 0, totalMin: 0 };
      codeOrder.push(ci.code);
    }
    byCode[ci.code].units += 1;
    byCode[ci.code].totalMin += (ci.target || 0);
  });
  const codeListInline = codeOrder.map(function(c) {
    const g = byCode[c];
    return c + (g.units > 1 ? ' ×' + g.units : '');
  }).join(' + ');

  const yy = parseInt(monthStr.split('-')[0]);
  const mm = parseInt(monthStr.split('-')[1]);
  const firstDay = monthStr + '-01';
  const lastDay = monthStr + '-' + new Date(yy, mm, 0).getDate();

  const lines = [];
  lines.push('BILLING VISIT NOTE — ' + monthLabel);
  lines.push('Patient: ' + patientName);
  if (providerName) lines.push('Billing Practitioner: ' + providerName);
  lines.push('Service Period: ' + firstDay + ' to ' + lastDay);
  lines.push('CPT Codes: ' + codeListInline);
  lines.push('Program: ' + tier.name);
  lines.push('');

  if (isApcm) {
    buildApcmBillingSummary(patient, codeListInline, tier, apcmDecision).forEach(function(line) {
      lines.push(line);
    });
    lines.push('');
    // When this APCM month is paired with G0570 (BHI add-on), append the
    // deterministic BHI add-on attestation block. The pure builder lives in
    // bhi-billing.js (CommonJS, Jest-testable). No minute language — G0570
    // is flat-fee and rides the APCM base code's audit footprint.
    const hasG0570 = cptCodes.some(function(c) { return c.code === 'G0570'; });
    if (hasG0570) {
      buildBhiAddonAttestationLines(patient, monthStr).forEach(function(line) { lines.push(line); });
      lines.push('');
    }
    // Same-bill PIN pairing: append the deterministic navigation block whenever
    // the bill carries G0023 so the note documents the nav-family time distinctly.
    if (cptCodes.some(function(c) { return c.code === 'G0023'; })) {
      buildPinAttestationLines(patient, monthStr, staffList, _formatEntryStaff).forEach(function(line) { lines.push(line); });
      lines.push('');
    }
    // Same-bill CPM pairing: append the deterministic pain block whenever the
    // bill carries G3002 so the note documents the pain-family time distinctly.
    if (cptCodes.some(function(c) { return c.code === 'G3002'; })) {
      buildPainAttestationLines(patient, monthStr, staffList, _formatEntryStaff).forEach(function(line) { lines.push(line); });
      lines.push('');
    }
  } else {
    lines.push(tier.enrollment);
    lines.push('');

    const condStr = conditionsToString(patient.conditions);
    if (condStr) {
      lines.push('CHRONIC CONDITIONS MANAGED:');
      condStr.split(';').forEach(function(c) {
        const trimmed = c.trim();
        if (trimmed) lines.push('- ' + trimmed);
      });
      lines.push('');
    }

    lines.push(aiText.trim());
    lines.push('');

    lines.push('TIME DOCUMENTED THIS MONTH: ' + totalMonthTime + ' minutes');
    codeOrder.forEach(function(c) {
      const g = byCode[c];
      const unitSuffix = g.units > 1 ? ' (×' + g.units + ')' : '';
      // Flat-fee codes (target=0, e.g. G0570) should not render ": 0 min".
      const minPart = (BILLING_TIME_TARGETS[c] || 0) > 0 ? ': ' + g.totalMin + ' min' : '';
      lines.push('- ' + c + unitSuffix + minPart + ' — ' + (g.desc || ''));
    });
    if (baseCode === '99487') {
      // Signature-bound (compliance review 2026-07-07): tier selection alone
      // cannot verify practitioner MDM — the signing practitioner attests it.
      lines.push('By signing, the billing practitioner attests that moderate-to-high complexity MDM was personally performed during this service period.');
    }
  }

  const entries = (activitySummary && activitySummary.entries) || [];
  if (!isApcm && entries.length) {
    lines.push('Itemized time log:');
    const sortedEntries = entries.slice().sort(function(a, b) {
      return (a.date || '').localeCompare(b.date || '');
    });
    sortedEntries.forEach(function(e) {
      const staffStr = _formatEntryStaff(e, staffList);
      const dur = parseInt(e.duration) || 0;
      const type = entryActivityType(e);
      const activityNote = entryActivityNote(e);
      const noteText = activityNote ? ' — ' + activityNote : '';
      lines.push('- ' + staffStr + ' — ' + dur + ' min — ' + type + noteText);
    });
  }
  lines.push('');

  // Same-bill PIN pairing on a CCM base: the itemized log above is CCM-family
  // only, so the navigation block documents its own distinct time.
  if (!isApcm && cptCodes.some(function(c) { return c.code === 'G0023'; })) {
    buildPinAttestationLines(patient, monthStr, staffList, _formatEntryStaff).forEach(function(line) { lines.push(line); });
    lines.push('');
  }

  // Same-bill CPM pairing on a CCM base: the itemized log above is CCM-family
  // only, so the pain block documents its own distinct time.
  if (!isApcm && cptCodes.some(function(c) { return c.code === 'G3002'; })) {
    buildPainAttestationLines(patient, monthStr, staffList, _formatEntryStaff).forEach(function(line) { lines.push(line); });
    lines.push('');
  }

  lines.push('COMPLIANCE ATTESTATION:');
  if (isApcm) {
    // APCM consent. Source priority: patient-level apcmConsent (when combined
    // setting is off and the operator documented it via the Fallback page) →
    // combined-CCM coverage (when the practice's combined-consent setting is
    // on) → CCM consent date (legacy fallback).
    const apcmConsent = patient.apcmConsent;
    const consentBasis = apcmDecision && apcmDecision.consentBasis;
    let consentLine;
    if (apcmConsent && apcmConsent.signed && apcmConsent.date) {
      consentLine = '- ' + (apcmConsent.source === 'written' ? 'Written' : 'Verbal') +
        ' consent for APCM was obtained on ' + apcmConsent.date + ' and remains active.';
    } else if (consentBasis === 'combined-ccm' && patient.consentDate) {
      consentLine = '- Consent for Care Management (covering both CCM and APCM under the practice\'s combined-consent policy) was obtained on ' +
        patient.consentDate + ' and remains active.';
    } else if (patient.consentDate) {
      consentLine = '- Consent for Care Management was obtained on ' + patient.consentDate + ' and remains active.';
    } else {
      // No recorded date — say exactly that instead of asserting standing consent.
      consentLine = '- Consent for Care Management was attested at enrollment (see audit log); the original consent date is not separately recorded.';
    }
    lines.push(consentLine);
    lines.push('- APCM eligibility requirements were satisfied. If the patient was new or had not been seen by the billing practitioner/practice within the prior 3 years, a qualifying initiating visit was completed unless a prior-year APCM/CCM/PCM service satisfied the CMS exception.');
    lines.push('- Patient has access to care management services and a designated care team member 24 hours per day, 7 days per week, including after-hours urgent needs.');
    lines.push('- Asynchronous communication channels (e.g., patient portal messaging, secure email) are available to the patient and any caregiver for non-urgent care needs.');
    lines.push('- Care coordination and communication with relevant clinicians, caregivers, and/or community resources is conducted as needed, including management of care transitions where applicable.');
    lines.push('- Medication management, including review of adherence, interactions, and safety, is part of the comprehensive care plan and is addressed as clinically indicated.');
    lines.push('- The comprehensive care plan was reviewed and updated as appropriate this month; a copy is provided in the patient\'s chart and made available to the patient and/or designated caregiver and other managing clinicians as appropriate.');
    tier.complianceExtra.forEach(function(line) { lines.push('- ' + line); });
    lines.push('- No CCM or PCM claim is being submitted for this same calendar month.');
  } else {
    // Patient-link consent (secondary path) is electronic, not verbal/written —
    // the attestation must reflect how consent was actually obtained.
    const consentLabel = patient.consentSource === 'patient-link'
      ? 'Electronic (patient-approved via secure link)'
      : (consentType === 'written' ? 'Written' : 'Verbal');
    const consentLine = patient.consentDate
      ? '- ' + consentLabel + ' consent for Chronic Care Management was obtained on ' + patient.consentDate + ' and remains active.'
      : '- ' + consentLabel + ' consent for Chronic Care Management was attested at enrollment (see audit log); the original consent date is not separately recorded.';
    lines.push(consentLine);
    lines.push('- Eligibility requirements for Chronic Care Management enrollment were satisfied, including completion of a qualifying initiating visit where required.');
    lines.push('- Patient has access to care management services and a designated care team member 24 hours per day, 7 days per week, including after-hours urgent needs.');
    lines.push('- Asynchronous communication channels (e.g., patient portal messaging, secure email) are available to the patient and any caregiver for non-urgent care needs.');
    lines.push('- Care coordination and communication with relevant clinicians, caregivers, and/or community resources is conducted as needed, including management of care transitions where applicable.');
    lines.push('- Medication management, including review of adherence, interactions, and safety, is part of the comprehensive care plan and is addressed as clinically indicated.');
    // 99487 requires establishment or substantial revision per service period;
    // 99490/99491 only require monitor-and-revise-as-clinically-indicated.
    const carePlanLine = baseCode === '99487'
      ? '- The comprehensive care plan was reviewed and updated as appropriate this month; a copy is provided in the patient\'s chart and made available to the patient and/or designated caregiver and other managing clinicians as appropriate.'
      : '- The comprehensive care plan is on file and is monitored and revised as clinically indicated; a copy is provided in the patient\'s chart and made available to the patient and/or designated caregiver and other managing clinicians as appropriate.';
    lines.push(carePlanLine);
    tier.complianceExtra.forEach(function(line) { lines.push('- ' + line); });
    lines.push('- Patient\'s chronic conditions place them at risk for acute exacerbation, hospitalization, and functional decline.');
    lines.push('- Total CCM time documented reflects non-face-to-face care and is exclusive of any separately billable evaluation and management services.');
  }

  const savedPlan = getBillingNoteCarePlan(cpHistory, patient.id, monthStr);
  if (savedPlan && savedPlan.text) {
    lines.push('');
    const planMonthLabel = savedPlan.month ? formatCpMonth(savedPlan.month) : monthLabel;
    lines.push('CARE PLAN ON FILE:');
    lines.push('- Care plan dated ' + planMonthLabel + ' is on file in the patient chart.');
  }
  return lines.join('\n');
}

// Load practiceSettings (for practiceName) + staffList + cpHistory in parallel.
async function loadContext(patientId) {
  const [s, staff, cps] = await Promise.allSettled([
    ccmApi.getSettings(),
    ccmApi.listStaff(),
    ccmApi.listCarePlans(),
  ]);
  const settings = s.status === 'fulfilled' ? s.value : null;
  const practiceName = settings?.practiceSettings?.practiceName || '';
  const aiModel = normalizeAiModel(settings?.aiModel || 'bedrock-sonnet');
  const consentType = settings?.practiceSettings?.consentType || 'verbal';
  const staffList = staff.status === 'fulfilled' ? staff.value : [];
  const cpData = cps.status === 'fulfilled' ? cps.value : {};
  const cpHistory = (cpData && cpData.care_plan) ? cpData.care_plan : (cpData || {});
  return { practiceName, aiModel, consentType, staffList, cpHistory };
}

// Audit-only signal: a billed note is being generated for a patient whose
// consent is flagged signed but carries no resolvable date. Dormant given the
// first-visit-date consent subsystem (consent.js dates consent to the patient's
// first Elation office visit), but the bulk-update route allowlists `consent`
// and `consentDate` independently and does not enforce the invariant — so we
// make the anomaly observable rather than silent. Pure; no I/O.
function _billingConsentMissingDate(patient, opts) {
  const p = (patient && patient._raw) ? patient._raw : (patient || {});
  if (opts && opts.bhi) {
    const c = p.bhiConsent;
    return !!(c && c.signed === true) && !(c && c.date) && !p.consentDate;
  }
  const ccmSigned = p.consent === 'signed';
  const apcmSigned = !!(p.apcmConsent && p.apcmConsent.signed === true);
  const hasDate = !!(p.consentDate || (p.apcmConsent && p.apcmConsent.date));
  return (ccmSigned || apcmSigned) && !hasDate;
}

// ── Public API ───────────────────────────────────────────────────────────────
// Mirrors v2's generateBillingNote (6648-6710) followed immediately by
// saveBillingNote (6713-6735) — same eligibility checks, prompt, AI call, note
// assembly, and per-CPT save.
//
// Returns { ok, note, cptCodes, totalMin, savedCount }.
// Throws on validation failure or AI failure with the v2 toast message.
export async function generateAndSaveBillingNote(patient, monthStr, options) {
  const opts = options || {};
  const p = patient?._raw || patient || {};
  const patientId = patient?.id || p.id;
  if (!patientId) throw new Error('Patient id missing');
  if (!monthStr) throw new Error('Select a patient and month.');

  // APCM fallback: when an operator has recorded an APCM tier for this month,
  // bill that G-code instead of the patient's CCM tier. The 2-chronic gate
  // doesn't apply to APCM (G0556 covers 0 or 1 chronic per NACHC), and the
  // tier code is used for note context downstream.
  const apcmMap = (p.apcmMonth && typeof p.apcmMonth === 'object') ? p.apcmMonth : {};
  const apcmDecision = apcmMap[monthStr] || null;
  const ccmCode = (p.billing || {}).program;

  let baseCode;
  if (apcmDecision && apcmDecision.code) {
    baseCode = apcmDecision.code;
  } else {
    // v2 line 6657 — patient must have a recognised base program assigned.
    if (!ccmCode || (!CCM_TIERS[ccmCode] && !PCM_BASE_CODES.has(ccmCode))) throw new Error('Patient does not have a CCM program assigned.');
    // Condition-count gates differ by family: CCM requires 2+ chronic
    // conditions; PCM manages ONE principal condition (>=1 documented).
    if (PCM_BASE_CODES.has(ccmCode)) {
      if (!pcmPrincipalCondition(p)) {
        throw new Error('PCM (99424/99426) requires an unambiguous principal condition: document exactly one condition, or set billing.pcmCondition to one of the documented condition descriptions.');
      }
    } else if (ccmChronicConditionCount(p) < 2) {
      throw new Error('CCM (99490/99491/99487) requires 2+ chronic conditions documented; this patient has ' + ccmChronicConditionCount(p) + '. Update the conditions list before generating a billing note.');
    }
    baseCode = ccmCode;
  }

  await loadBnTemplate();
  const { practiceName, aiModel, consentType, staffList, cpHistory } = await loadContext(patientId);

  // v2 line 6668 — monthly activity summary from the patient's activityLog,
  // filtered to CCM-family entries only so BHI time doesn't leak into the
  // CCM billing-note total. Legacy untagged entries are treated as 'ccm'
  // per the entryProgramFamily contract.
  const summary = getMonthlyActivitySummary(p, monthStr, 'ccm');

  // v2 line 6673-6676 — zero entries is a fraud-risk guard for CCM. APCM is a
  // flat-fee monthly program and can be billed from the recorded APCM decision.
  if (!apcmDecision && (!summary.entries || summary.entries.length === 0)) {
    throw new Error('No CCM activity logged for ' + formatCpMonth(monthStr) + '. Add activity log entries before generating a billing note — a time override does not substitute for documented activity.');
  }

  // CCM tier auto-bump: when the patient has an affirmed ccmTierMonth override
  // for this month and >= 90 CCM minutes logged, bill the higher tier. APCM
  // months keep their G-code (apcmDecision already set baseCode above). The
  // >=90 guard inside effectiveCcmCodeForMonth makes this auto-revert if minutes
  // fell below 90 after the affirmation.
  if (!apcmDecision) {
    baseCode = effectiveCcmCodeForMonth(p, monthStr, summary.total);
  }

  const overrideTime = opts.overrideMinutes ? parseInt(opts.overrideMinutes) : null;
  const totalTime = overrideTime || summary.total;
  // When an APCM decision exists for this month, force the G-code as the bill
  // code. Otherwise the patient's CCM program drives codes (unchanged path).
  const cptCodes = getCcmBillingCodes(p, totalTime, apcmDecision ? apcmDecision.code : baseCode, {
    monthStr, auditOnDrop: true, staffList, painScaleOverride: opts.painScaleOverride === true,
  });
  if (!cptCodes.length) throw new Error('No billable CPT codes for this patient/time.');

  // Audit-trail the signed-without-date consent anomaly (see
  // _billingConsentMissingDate). Fire-and-forget; no chart or billing change.
  if (_billingConsentMissingDate(p)) {
    safeAudit('CONSENT_NO_DATE', patientId,
      'Billed ' + baseCode + ' note generated for ' + monthStr + ' with consent flagged signed but no consent date on file.'
    ).catch(() => {});
  }

  let aiText = '';
  if (!apcmDecision) {
    // v2 line 6685-6686 — one AI call for CCM goals + interventions. APCM
    // notes are assembled deterministically from structured audit attestations.
    // Split into a cacheable tier-template prefix + per-patient suffix so the
    // static template head caches across a bulk run (cachedPrefix '' → callAI
    // sends a single non-cached string, preserving prior behavior).
    const { cachedPrefix, dynamicSuffix } = buildBillingNoteAIPromptCached(p, summary, baseCode, monthStr);
    // Billing notes for multi-condition patients regularly run past 4096
    // output tokens (activity summary + CPT rationale + compliance attestation
    // + care-plan footer). 8192 is the safe ceiling across every Bedrock model
    // alias on the allowlist (Haiku 4.5 maxes at 8192; Sonnet/Opus support more
    // if a future flow opts in).
    aiText = await callAI(dynamicSuffix, aiModel, { max_tokens: 8192, cachedPrefix: cachedPrefix || undefined });

    // v2 line 6687-6689 — re-identify [PATIENT_NAME] tokens locally.
    const bnPhiMap = { '[PATIENT_NAME]': ((p.fname || '') + ' ' + (p.lname || '')).trim() || 'patient' };
    aiText = reidentifyText(aiText, bnPhiMap);
  }

  // v2 line 6692 — assemble ONE combined note covering every CPT.
  const combinedText = assembleCombinedBillingNote(p, monthStr, aiText, cptCodes, totalTime, summary, staffList, practiceName, cpHistory, apcmDecision, consentType, baseCode);

  // v2 saveBillingNote (line 6713-6735): persist the single combined note
  // under type=BN_COMBINED_KEY. v2 stores into _bnHistory[pid][monthStr][key],
  // then dbPosts. We skip the local cache (v3 useDraft refetches) and just save.
  await api.post('/data/careplans/' + encodeURIComponent(patientId) + '/' + monthStr, {
    plan_text: combinedText,
    type: BN_COMBINED_KEY,
  });

  invalidateDraftCache();

  return { ok: true, note: combinedText, cptCodes, totalMin: totalTime, savedCount: 1 };
}

// ── Batch lane: CCM combined billing-note record builder + materializer ───────
// Additive extraction for the batch orchestrator. generateAndSaveBillingNote
// above is byte-untouched.

// MIRROR of generateAndSaveBillingNote's pre-callAI build (~:858-931); keep in sync — Task 4b enforces builder parity.
// Builds the SAME prompt/codes/context but returns a batch record instead of
// calling callAI. APCM months skip the LLM entirely (the realtime path assembles
// them deterministically) — we surface that with skipBatch so the orchestrator
// can materialize the APCM note locally with aiText=''. If you change the
// pre-callAI sequence above, MIRROR it here.
export async function buildBillingRecord(patient, monthStr, opts) {
  const o = opts || {};
  const p = patient?._raw || patient || {};
  const patientId = patient?.id || p.id;
  if (!patientId) throw new Error('Patient id missing');
  if (!monthStr) throw new Error('Select a patient and month.');

  // APCM fallback decision for this month (same lookup as the realtime path).
  const apcmMap = (p.apcmMonth && typeof p.apcmMonth === 'object') ? p.apcmMonth : {};
  const apcmDecision = apcmMap[monthStr] || null;
  const ccmCode = (p.billing || {}).program;

  let baseCode;
  if (apcmDecision && apcmDecision.code) {
    baseCode = apcmDecision.code;
  } else {
    if (!ccmCode || (!CCM_TIERS[ccmCode] && !PCM_BASE_CODES.has(ccmCode))) throw new Error('Patient does not have a CCM program assigned.');
    if (PCM_BASE_CODES.has(ccmCode)) {
      if (!pcmPrincipalCondition(p)) {
        throw new Error('PCM (99424/99426) requires an unambiguous principal condition: document exactly one condition, or set billing.pcmCondition to one of the documented condition descriptions.');
      }
    } else if (ccmChronicConditionCount(p) < 2) {
      throw new Error('CCM (99490/99491/99487) requires 2+ chronic conditions documented; this patient has ' + ccmChronicConditionCount(p) + '. Update the conditions list before generating a billing note.');
    }
    baseCode = ccmCode;
  }

  await loadBnTemplate();
  const { practiceName, aiModel, consentType, staffList, cpHistory } = await loadContext(patientId);

  const summary = getMonthlyActivitySummary(p, monthStr, 'ccm');

  if (!apcmDecision && (!summary.entries || summary.entries.length === 0)) {
    throw new Error('No CCM activity logged for ' + formatCpMonth(monthStr) + '. Add activity log entries before generating a billing note — a time override does not substitute for documented activity.');
  }

  if (!apcmDecision) {
    baseCode = effectiveCcmCodeForMonth(p, monthStr, summary.total);
  }

  const overrideTime = o.overrideMinutes ? parseInt(o.overrideMinutes) : null;
  const totalTime = overrideTime || summary.total;
  const cptCodes = getCcmBillingCodes(p, totalTime, apcmDecision ? apcmDecision.code : baseCode, {
    monthStr, auditOnDrop: true, staffList, painScaleOverride: o.painScaleOverride === true,
  });
  if (!cptCodes.length) throw new Error('No billable CPT codes for this patient/time.');

  if (_billingConsentMissingDate(p)) {
    safeAudit('CONSENT_NO_DATE', patientId,
      'Billed ' + baseCode + ' note generated for ' + monthStr + ' with consent flagged signed but no consent date on file.'
    ).catch(() => {});
  }

  // assemblerCtx mirrors EXACTLY the locals assembleCombinedBillingNote(...) is
  // called with at the realtime save site (~:939) minus aiText. The patient
  // subset carries every field the assembler reads (id/fname/lname/provider/
  // billing/conditions/apcmConsent/apcmMonth/consentDate/consentSource) PLUS
  // pin/pain + activityLog, which the nested attestation builders read when
  // the bill carries G0023/G3002. phiMap matches the realtime reidentify map.
  const phiMap = { '[PATIENT_NAME]': ((p.fname || '') + ' ' + (p.lname || '')).trim() || 'patient' };
  const assemblerCtx = {
    patient: {
      id: p.id, fname: p.fname, lname: p.lname, provider: p.provider,
      billing: p.billing, conditions: p.conditions,
      apcmConsent: p.apcmConsent, apcmMonth: p.apcmMonth, consentDate: p.consentDate,
      consentSource: p.consentSource,
      // Pairing evidence — a materialized note must retain the PIN/CPM records
      // and their distinct family logs.
      pin: p.pin, pain: p.pain, activityLog: p.activityLog,
    },
    monthStr,
    cptCodes,
    totalMonthTime: totalTime,
    activitySummary: summary,
    staffList,
    practiceName,
    cpHistory,
    apcmDecision,
    consentType,
    effectiveCcmCode: baseCode,
    phiMap,
  };

  // APCM: realtime skips the LLM (deterministic note). Tell the orchestrator to
  // assemble it locally with aiText='' rather than submit it to the batch.
  if (apcmDecision) {
    return { skipBatch: true, reason: 'apcm-no-llm', assemblerCtx };
  }

  const { cachedPrefix, dynamicSuffix } = buildBillingNoteAIPromptCached(p, summary, baseCode, monthStr);
  return {
    type: 'bn_combined',
    model: aiModel,
    promptBody: { max_tokens: 8192, temperature: 0.3, messages: buildUserMessages(dynamicSuffix, cachedPrefix || undefined) },
    assemblerCtx,
  };
}

// Materialize a batch CCM combined-note result into the exact note the realtime
// path would have saved, then persist it. Used ONLY by the batch orchestrator
// (for batch results AND the APCM/no-LLM local path with rawText=''). The CCM
// assembler is ESM here, so it is injected into the CJS materializer.
export async function materializeBillingResult(patient, monthStr, rawText, ctx) {
  const patientId = (patient?._raw || patient || {}).id || patient?.id;
  const note = buildMaterializedNote('bn_combined', rawText, ctx, { assembleCombined: assembleCombinedBillingNote });
  await api.post('/data/careplans/' + encodeURIComponent(patientId) + '/' + monthStr, { plan_text: note, type: BN_COMBINED_KEY });
  invalidateDraftCache();
  return { ok: true, note, cptCodes: ctx.cptCodes, totalMin: ctx.totalMonthTime, savedCount: 1 };
}

// ── BHI 99484 standalone billing note ────────────────────────────────────────
// Mirrors generateAndSaveBillingNote but for the BHI program. Gates check
// 99484-specific requirements (enrollment + consent + assessment + 20 min),
// the prompt template is BHI-specific, and the note is persisted under
// BHI_BN_KEY. The assembled note is produced by assembleBhiBillingNote
// (imported from ./bhi-billing — pure, Jest-readable).
//
// Returns { ok, note, cptCodes, totalMin, savedCount }.
export async function generateAndSaveBhiBillingNote(patient, monthStr, options) {
  const opts = options || {};
  const p = patient?._raw || patient || {};
  const patientId = patient?.id || p.id;
  if (!patientId) throw new Error('Patient id missing');
  if (!monthStr) throw new Error('Select a patient and month.');

  // Gate check (99484 — minute target required since this path is non-APCM
  // by construction; G0570 add-on rides on the APCM combined note instead).
  // opts.assessmentOverride demotes the missing-assessment blocker to a soft
  // warning so an explicitly-confirmed generate proceeds; enrollment, consent
  // and the minute floor still throw. The assembled note's attestation is
  // softened downstream (assembleBhiBillingNote omits the "assessment
  // documented" line when none is on file), so no false attestation results.
  const assessmentOverride = !!opts.assessmentOverride;
  const gates = bhiGatesForCode(p, monthStr, '99484', { assessmentOverride });
  if (!gates.ok) throw new Error('BHI not billable: ' + gates.blockers.join('; '));

  // True only when the operator explicitly overrode AND no assessment is
  // actually on file (the gate surfaced the missing-assessment warning).
  // Drives both the audit AND the template-agnostic prompt override directive.
  const noAssessmentOverride = assessmentOverride && (gates.warnings || []).includes('Initial assessment missing');

  // AUDIT the override at the point it takes effect: the operator has
  // explicitly chosen to generate a billed BHI note with no initial
  // rating-scale assessment on file. Only fires when the override actually
  // suppressed a real missing-assessment warning (not on every generate).
  if (noAssessmentOverride) {
    safeAudit('BHI_NO_ASSESSMENT_OVERRIDE', patientId,
      'BHI 99484 note generated for ' + monthStr + ' with the initial rating-scale assessment gate overridden — no PHQ-9/PHQ-2 assessment on file for the period. Operator explicitly proceeded.'
    ).catch(() => {});
  }

  // Load BHI prompt template + look up whether a care_plan row exists for
  // this (patient, month). The orchestrator (generateForPatient in push-emr.js)
  // is responsible for auto-building the comprehensive care plan when
  // missing — by the time we get here, the care plan should already be on
  // file. carePlanOnFile threads through to the pure assembler as
  // defense-in-depth: if any code path ever calls this generator directly
  // without going through generateForPatient, the assembler still produces
  // a truthful attestation (or omits the line).
  const [settings, cpData] = await Promise.all([
    ccmApi.getSettings(),
    ccmApi.listCarePlans().catch(function() { return null; }),
  ]);
  const bhiTpl = (settings && settings.bulk_bhi_bn_template) || BHI_BN_DEFAULT;
  const aiModel = normalizeAiModel(settings?.aiModel || 'bedrock-sonnet');
  const cpHistory = (cpData && cpData.care_plan) ? cpData.care_plan : (cpData || {});
  const carePlanText = (cpHistory && cpHistory[patientId] && cpHistory[patientId][monthStr]) || '';
  const carePlanOnFile = typeof carePlanText === 'string' && carePlanText.trim().length > 0;

  // Activity summary filtered to BHI-tagged entries only. The shared
  // getMonthlyActivitySummary helper handles the filter — see the
  // programFilter argument introduced for symmetric program-aware
  // filtering across CCM and BHI.
  const summary = getMonthlyActivitySummary(p, monthStr, 'bhi');

  const overrideTime = opts.overrideMinutes ? parseInt(opts.overrideMinutes) : null;
  const totalTime = overrideTime || summary.total;
  const rateInfo = DEFAULT_RATES['99484'] || {};
  const cptCodes = [{ code: '99484', desc: rateInfo.desc || 'BHI Care Mgmt, per calendar month', target: 20, met: totalTime >= 20 }];

  // Audit-trail the signed-without-date consent anomaly for the BHI path too.
  if (_billingConsentMissingDate(p, { bhi: true })) {
    safeAudit('CONSENT_NO_DATE', patientId,
      'Billed 99484 BHI note generated for ' + monthStr + ' with BHI consent flagged signed but no consent date on file.'
    ).catch(() => {});
  }

  // Build the BHI prompt (separate template + vars from the CCM/APCM path)
  // and call the AI for the GOALS + INTERVENTIONS body. In the no-assessment
  // override case, append the template-agnostic override directive to the FINAL
  // prompt so it holds even if a custom template still references a scale.
  const prompt = bnApplyNoAssessmentOverrideDirective(
    bcpInterpolate(bhiTpl, bnBuildPromptVars(p, summary, '99484', monthStr)),
    noAssessmentOverride,
  );
  let aiText = await callAI(prompt, aiModel, { max_tokens: 4096 });
  const bnPhiMap = { '[PATIENT_NAME]': ((p.fname || '') + ' ' + (p.lname || '')).trim() || 'patient' };
  aiText = reidentifyText(aiText, bnPhiMap);

  // Assemble (pure) + persist under the BHI key so it's distinguishable from
  // the CCM combined note in the careplans store.
  const noteText = assembleBhiBillingNote(p, monthStr, aiText, cptCodes, totalTime, summary, carePlanOnFile);
  await api.post('/data/careplans/' + encodeURIComponent(patientId) + '/' + monthStr, {
    plan_text: noteText,
    type: BHI_BN_KEY,
  });

  // Targeted cache invalidation — only the BHI draft for this patient/month.
  invalidateDraftCache(patientId, monthStr, 'bhi');

  return { ok: true, note: noteText, cptCodes, totalMin: totalTime, savedCount: 1 };
}

// ── Batch lane: BHI 99484 billing-note record builder + materializer ──────────
// Additive extraction for the batch orchestrator. generateAndSaveBhiBillingNote
// above is byte-untouched.

// MIRROR of generateAndSaveBhiBillingNote's gate + pre-callAI build (~:962-1019);
// keep in sync — Task 4b enforces builder parity. Builds the SAME prompt/codes/
// context but returns a batch record instead of calling callAI. The BHI gate is
// preserved (throws 'BHI not billable: ...'). The assemblerCtx shape matches the
// Task-5 parity test, which proved it reconstructs a byte-identical BHI note.
export async function buildBhiBillingRecord(patient, monthStr, opts) {
  const o = opts || {};
  const p = patient?._raw || patient || {};
  const patientId = patient?.id || p.id;
  if (!patientId) throw new Error('Patient id missing');
  if (!monthStr) throw new Error('Select a patient and month.');

  // Parity with generateAndSaveBhiBillingNote: o.assessmentOverride demotes the
  // missing-assessment blocker to a soft warning + audits the override.
  const assessmentOverride = !!o.assessmentOverride;
  const gates = bhiGatesForCode(p, monthStr, '99484', { assessmentOverride });
  if (!gates.ok) throw new Error('BHI not billable: ' + gates.blockers.join('; '));

  const noAssessmentOverride = assessmentOverride && (gates.warnings || []).includes('Initial assessment missing');

  if (noAssessmentOverride) {
    safeAudit('BHI_NO_ASSESSMENT_OVERRIDE', patientId,
      'BHI 99484 note generated for ' + monthStr + ' with the initial rating-scale assessment gate overridden — no PHQ-9/PHQ-2 assessment on file for the period. Operator explicitly proceeded.'
    ).catch(() => {});
  }

  const [settings, cpData] = await Promise.all([
    ccmApi.getSettings(),
    ccmApi.listCarePlans().catch(function() { return null; }),
  ]);
  const aiModel = normalizeAiModel(settings?.aiModel || 'bedrock-sonnet');
  const cpHistory = (cpData && cpData.care_plan) ? cpData.care_plan : (cpData || {});
  const carePlanText = (cpHistory && cpHistory[patientId] && cpHistory[patientId][monthStr]) || '';
  const carePlanOnFile = typeof carePlanText === 'string' && carePlanText.trim().length > 0;

  const summary = getMonthlyActivitySummary(p, monthStr, 'bhi');

  const overrideTime = o.overrideMinutes ? parseInt(o.overrideMinutes) : null;
  const totalTime = overrideTime || summary.total;
  const rateInfo = DEFAULT_RATES['99484'] || {};
  const cptCodes = [{ code: '99484', desc: rateInfo.desc || 'BHI Care Mgmt, per calendar month', target: 20, met: totalTime >= 20 }];

  if (_billingConsentMissingDate(p, { bhi: true })) {
    safeAudit('CONSENT_NO_DATE', patientId,
      'Billed 99484 BHI note generated for ' + monthStr + ' with BHI consent flagged signed but no consent date on file.'
    ).catch(() => {});
  }

  const bhiTpl = (settings && settings.bulk_bhi_bn_template) || BHI_BN_DEFAULT;
  // Same template-agnostic no-assessment override directive as the realtime
  // path — appended to the FINAL prompt so a custom template can't reintroduce
  // a false rating-scale assertion.
  const prompt = bnApplyNoAssessmentOverrideDirective(
    bcpInterpolate(bhiTpl, bnBuildPromptVars(p, summary, '99484', monthStr)),
    noAssessmentOverride,
  );

  // assemblerCtx mirrors EXACTLY the args assembleBhiBillingNote(patient,
  // monthStr, aiText, cptCodes, totalMonthTime, activitySummary, carePlanOnFile)
  // reads, minus aiText. The patient subset carries every field that assembler
  // reads (fname/lname/provider/conditions/consentDate/bhi/bhiConsent/billing).
  // phiMap matches the realtime reidentify map. (BHI has no cache split.)
  const phiMap = { '[PATIENT_NAME]': ((p.fname || '') + ' ' + (p.lname || '')).trim() || 'patient' };
  const assemblerCtx = {
    patient: {
      id: p.id, fname: p.fname, lname: p.lname, provider: p.provider,
      conditions: p.conditions, consentDate: p.consentDate,
      bhi: p.bhi, bhiConsent: p.bhiConsent, billing: p.billing,
    },
    monthStr,
    cptCodes,
    totalMonthTime: totalTime,
    activitySummary: summary,
    carePlanOnFile,
    phiMap,
  };

  return {
    type: 'bhi_bn',
    model: aiModel,
    promptBody: { max_tokens: 4096, temperature: 0.3, messages: buildUserMessages(prompt) },
    assemblerCtx,
  };
}

// Materialize a batch BHI result into the exact note the realtime path would
// have saved, then persist it. Used ONLY by the batch orchestrator. The BHI
// assembler is CJS inside batch-materialize, so no injection is needed.
//
// carePlanOnFile is RECOMPUTED here, not taken from ctx. The batch builder
// (buildBhiBillingRecord) freezes carePlanOnFile at COLLECT time — before any
// care plan was saved this run — so for the normal month-end case it freezes
// `false`. By the time this materializer runs, materializeBatchResults has
// already applied the care_plan row for this (patient, month) first
// (orderResultsCarePlanFirst), so a fresh read of the live care-plan store
// yields the realtime-equivalent value. We read the store once and recompute
// via carePlanOnFileForMonth (which replicates the exact-month presence
// semantics of generateAndSaveBhiBillingNote), then override ctx before
// assembling so the materialized note matches the realtime note's attestation.
export async function materializeBhiBillingResult(patient, monthStr, rawText, ctx) {
  const patientId = (patient?._raw || patient || {}).id || patient?.id;
  let carePlanOnFile = ctx.carePlanOnFile;
  try {
    const cpData = await ccmApi.listCarePlans();
    const cpHistory = (cpData && cpData.care_plan) ? cpData.care_plan : (cpData || {});
    carePlanOnFile = carePlanOnFileForMonth(cpHistory, patientId, monthStr);
  } catch (_) {
    // Live store unreadable → fall back to the frozen ctx value (defense-in-depth).
  }
  const note = buildMaterializedNote('bhi_bn', rawText, { ...ctx, carePlanOnFile }, {});
  await api.post('/data/careplans/' + encodeURIComponent(patientId) + '/' + monthStr, { plan_text: note, type: BHI_BN_KEY });
  invalidateDraftCache(patientId, monthStr, 'bhi');
  return { ok: true, note, cptCodes: ctx.cptCodes, totalMin: ctx.totalMonthTime, savedCount: 1 };
}
